import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { filter, forkJoin, Observable, of, switchMap } from 'rxjs';
import { Extension } from 'src/app/models/centrex/extension';
import { Rule } from 'src/app/models/centrex/rule';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { RuleService } from 'src/app/services/centrex/rule.service';
import { DialogDeleteConfirmationComponent } from '../../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';
import { ExtensionDisplayPipe } from 'src/app/pipes/extension-display.pipe';
import { RuleTargetTypes } from 'src/app/models/centrex/enums/rule-target-types';
import { RingStates } from 'src/app/models/centrex/enums/ring-states';
import { ServiceService } from 'src/app/services/centrex/service.service';
import { Service } from 'src/app/models/centrex/service';
import { ExtensionDisplayPipe as ExtensionDisplayPipe_1 } from '../../../pipes/extension-display.pipe';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSuffix, MatFormField } from '@angular/material/form-field';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-extension-rules',
    templateUrl: './extension-rules.component.html',
    styleUrls: ['./extension-rules.component.scss'],
    imports: [
        MatButton,
        RouterLink,
        MatIcon,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatCheckbox,
        MatIconButton,
        MatSuffix,
        MatTooltip,
        MatCellDef,
        MatCell,
        MatFormField,
        MatInput,
        MatSelect,
        MatOption,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        TranslateModule,
        ExtensionDisplayPipe_1,
    ]
})
export class ExtensionRulesComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    extensions: Extension[] = [];
    services: Service[] = [];

    displayedColumns: string[] = ['enabled', 'ringState', 'targetType', 'ringSeconds', 'target', 'actions'];
    dataSource: MatTableDataSource<Rule>;
    deleteConfirmationDialog: MatDialogRef<DialogDeleteConfirmationComponent>;

    filtersForm: UntypedFormGroup;

    checkBoxStatus: boolean = null;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    ringStates = Object.keys(RingStates);
    ruleTargetTypes = Object.keys(RuleTargetTypes);

    private _extension: Extension;

    @Input() set extension(value: Extension) {
        this._extension = value;
    }

    get extension(): Extension {
        return this._extension;
    }

    private _extensionsList: Extension[] = null;

    @Input() set extensionsList(value: Extension[]) {
        if (value) {
            this._extensionsList = value;
        }
    }

    get extensionsList(): Extension[] {
        return this._extensionsList;
    }

    constructor(
        private extensionService: ExtensionService,
        private serviceService: ServiceService,
        private ruleService: RuleService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService,
        private extensionDisplayPipe: ExtensionDisplayPipe
    ) {
        this.filtersForm = new UntypedFormGroup({});
    }

    ngOnInit(): void {
        this.initFiltersForm();
        this.loadDatas(this.extension);
    }

    /**
     * Initialisation du formulaire des filtres du tableau des règles
     */
    initFiltersForm() {
        this.filtersForm = this.formbuilder.group({
            ringState: [''],
            targetType: [''],
            ringSeconds: [''],
            enabled: [this.checkBoxStatus],
            target: [''],
        });
    }

    loadDatas(extension: Extension) {
        this.spinner.show();

        let datas: Observable<[Extension[], Service[], Rule[]]>;

        if (!this.extensionsList) {
            datas = forkJoin([this.extensionService.getExtensions(), this.serviceService.getServices(), this.ruleService.getExtensionRules(extension.id)]);
        } else {
            datas = forkJoin([of(this.extensionsList), this.serviceService.getServices(), this.ruleService.getExtensionRules(extension.id)]);

            console.log('extension-rules: OK');
        }

        datas.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: ([extensions, services, rules]) => {
                this.extensions = extensions;
                this.services = services;

                let extensionsRules = rules.filter((a) => !!a.extensionId);

                extensionsRules.forEach((rule) => {
                    rule['extension'] = extensions.find((a) => a.id === rule.extensionId);

                    if (rule.targetType === RuleTargetTypes.extension) {
                        rule['targetDisplay'] = this.extensionDisplayPipe.transform(extensions.find((a) => a.id === rule.target));
                    } else if (rule.targetType === RuleTargetTypes.service) {
                        rule['targetDisplay'] = services.find((a) => a.name === rule.target).name;
                    } else {
                        rule['targetDisplay'] = rule.target;
                    }
                });

                this.dataSource = new MatTableDataSource(extensionsRules);

                this.dataSource.paginator = this.paginator;
                this.dataSource.sort = this.sort;

                this.createFilters();

                this.spinner.hide();
            },
            error: () => {
                this.spinner.hide();
            },
        });
    }

    createFilters() {
        this.dataSource.filterPredicate = (data: Rule, filter: string) => {
            const filterArray = filter.split('¤');
            const matchFilter = [];

            if (filterArray[0] !== '') {
                matchFilter.push(data.enabled === Boolean(JSON.parse(filterArray[0])));
            }

            const ringStates = filterArray[1] ? filterArray[1].split(',') : null;

            if (ringStates) {
                matchFilter.push(data.ringState && ringStates.includes(data.ringState.trim().toLowerCase()));
            }

            const targetTypes = filterArray[2] ? filterArray[2].split(',') : null;

            if (targetTypes) {
                matchFilter.push(data.targetType && targetTypes.includes(data.targetType.trim().toLowerCase()));
            }

            if (filterArray[3] !== '') {
                matchFilter.push(data.ringSeconds && data.ringSeconds.toString().trim().toLowerCase().includes(filterArray[3].trim().toLowerCase()));
            }

            if (filterArray[4] !== '') {
                matchFilter.push(data['targetDisplay'] && data['targetDisplay'].trim().toLowerCase().includes(filterArray[4].trim().toLowerCase()));
            }

            return matchFilter.every(Boolean);
        };

        this.displayedColumns = ['enabled', 'ringState', 'targetType', 'ringSeconds', 'target', 'actions'];

        this.applyFilters();
    }

    /**
     * Application du filtre dans le tableau des règles
     */
    applyFilters() {
        const enabled = this.filtersForm.get('enabled').value;
        const ringState = this.filtersForm.get('ringState').value;
        const targetType = this.filtersForm.get('targetType').value;
        const ringSeconds = this.filtersForm.get('ringSeconds').value;
        const target = this.filtersForm.get('target').value;

        const filterValue =
            (enabled !== null ? enabled : '') +
            '¤' +
            (!!ringState ? ringState : '') +
            '¤' +
            (!!targetType ? targetType : '') +
            '¤' +
            (!!ringSeconds ? ringSeconds : '') +
            '¤' +
            (!!target ? target : '');

        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }
    /**
     * Réinitialise le filtre de la colonne spécifiée.
     * @param formControlName Nom du contrôle du formulaire
     */
    resetColumnFilter(formControlName: string): void {
        if (formControlName === 'enabled') {
            this.checkBoxStatus = null;
        }

        this.filtersForm.controls[formControlName].reset();
        this.applyFilters();
    }

    /**
     * Réinitialise tous les filtres de colonne
     */
    resetAllColumnFilter() {
        this.checkBoxStatus = null;
        this.filtersForm.reset();
        this.applyFilters();
    }

    onDeleteClick(rule: Rule) {
        this.deleteConfirmationDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        this.deleteConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogDeleteConfirmation.RuleExtensionConfirmMessage', {
            ruleId: rule.id,
            extensionName: this.extensionDisplayPipe.transform(rule['extension']),
        });

        this.deleteConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.ruleService.deleteExtensionRule((rule['extension'] as Extension).id, rule.id)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.toastr.success(
                        this.translate.instant('RulesByExtensions.SuccessMessageDeleteExtensionRule', {
                            extensionName: this.extensionDisplayPipe.transform(rule['extension']),
                        })
                    );
                    this.loadDatas(this.extension);
                },
                error: () => {
                    this.toastr.error(
                        this.translate.instant('RulesByExtensions.ErrorMessageDeleteExtensionRule', {
                            extensionName: this.extensionDisplayPipe.transform(rule['extension']),
                        })
                    );
                },
                complete: () => {
                    this.deleteConfirmationDialog = null;
                },
            });
    }

    checkBoxChange() {
        switch (this.checkBoxStatus) {
            case false:
                this.checkBoxStatus = null;
                this.filtersForm.get('enabled').setValue(null);
                break;

            case null:
                this.checkBoxStatus = true;
                break;

            case true:
                this.checkBoxStatus = false;
                break;
        }

        this.applyFilters();
    }
}
